import React, { useState } from "react";
import { Card } from "flowbite-react";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';

interface NotificationSettingsProps {
  enabled: boolean;
  minutesBefore: number;
  soundEnabled: boolean;
  nextPrayer: string;
  onSave: (enabled: boolean, minutesBefore: number, soundEnabled: boolean) => void;
}

const NotificationSettings = ({
  enabled,
  minutesBefore,
  soundEnabled,
  nextPrayer,
  onSave,
}: NotificationSettingsProps) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [minutes, setMinutes] = useState(minutesBefore);
  const [isSoundEnabled, setIsSoundEnabled] = useState(soundEnabled);
  const [hasChanges, setHasChanges] = useState(false);

  const handleEnabledChange = (checked: boolean) => {
    setIsEnabled(checked);
    setHasChanges(true);
  };

  const handleMinutesChange = (value: number[]) => {
    const newMinutes = value[0];
    setMinutes(newMinutes);
    setHasChanges(true);
  };

  const handleSoundEnabledChange = (checked: boolean) => {
    setIsSoundEnabled(checked);
    setHasChanges(true);
  };

  const handleSave = () => {
    const settingsToSave = {
      enabled: isEnabled,
      reminderTime: minutes,
      soundEnabled: isSoundEnabled,
      nextPrayer: nextPrayer
    };
    localStorage.setItem('notificationSettings', JSON.stringify(settingsToSave));
    
    onSave(isEnabled, minutes, isSoundEnabled);
    setHasChanges(false);
  };

  return (
    <Card className="w-full mx-auto overflow-hidden animate-fadeIn shadow-lg">
      <div className="bg-purple-600 text-white p-4 rounded-t-lg">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Bell className="h-5 w-5 text-purple-200" />
          <h2 className="text-xl font-bold">{t('notifications')}</h2>
        </div>
      </div>
      
      <div className="p-6 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-medium">{t('enableNotifications')}</span>
            <Switch checked={isEnabled} onCheckedChange={handleEnabledChange} />
          </div>
          
          {isEnabled && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span>
                    {t('remindMeBeforeIftar', { minutes: minutes, prayer: t(nextPrayer) })}
                  </span>
                </div>
                <Slider
                  defaultValue={[minutes]}
                  min={5}
                  max={60}
                  step={5}
                  onValueChange={handleMinutesChange}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span>{t('soundEnabled')}</span>
                <Switch checked={isSoundEnabled} onCheckedChange={handleSoundEnabledChange} />
              </div>
            </>
          )}
        </div>
        
        {isEnabled && (
          <div className="mt-6">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
            >
              {t('saveSettings')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default NotificationSettings;
